import { useEffect, useRef, useState } from 'react';
import { SKELETON } from '@constants/motion';
import { crossfade } from '@lib/utils/motion';

/**
 * Whether a loading placeholder should be visible: only once loading has lasted a moment (no flash on a fast
 * answer), and then for a minimum time (no flicker when the answer arrives right after it appeared). The
 * placeholder gives way to the content with a short crossfade (the motion standard: skeleton -> content).
 */
export function useLoadingGate(loading: boolean) {
  const [visible, setVisible] = useState(false);
  const shownAt = useRef(0);
  useEffect(() => {
    if (loading && !visible) {
      const t = window.setTimeout(() => {
        shownAt.current = performance.now();
        setVisible(true);
      }, SKELETON.delayMs);
      return () => window.clearTimeout(t);
    }
    if (!loading && visible) {
      const left = SKELETON.minVisibleMs - (performance.now() - shownAt.current);
      const hide = () => crossfade(() => setVisible(false));
      if (left <= 0) {
        hide();
        return;
      }
      const t = window.setTimeout(hide, left);
      return () => window.clearTimeout(t);
    }
  }, [loading, visible]);
  return visible;
}
